import { Component, computed, inject, Signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { environment } from '../../../../../environment/environment';
import { EntitySelectComponent } from '../../shared/components/entity-select/entity-select.component';
import { AuthService, DemoService } from '../../../../core/services';
import { Partner } from '../../../../core/models';
import { AuthStore, DemoStore, NavbarStore, SelectedPartnerStore } from '../../../../core/stores';
import * as Paths from '../../../../core/constants';
import { LanguageEnum, Role } from '../../../../core/enums';

@Component({
  standalone: true,
  selector: 'dbx-partner-navbar',
  imports: [TranslateModule, MatMenuModule, MatIcon, RouterLink, MatButton, EntitySelectComponent, MatToolbarModule],
  providers: [NavbarStore],
  templateUrl: 'partner-navbar.component.html',
  styleUrl: 'partner-navbar.component.scss',
})
export class PartnerNavbarComponent {
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly demoService = inject(DemoService);
  private readonly authStore = inject(AuthStore);
  private readonly demoStore = inject(DemoStore);
  private readonly selectedPartnerStore = inject(SelectedPartnerStore);
  private readonly store = inject(NavbarStore);

  authenticated = this.authStore.authenticated;
  $partners: Signal<Partner[]> = this.store.partners;
  $partnerLoading: Signal<boolean> = this.store.partnerLoading;
  $partnerCompleted: Signal<boolean> = this.store.partnerCompleted;
  $selectedPartner: Signal<Partner | null> = this.selectedPartnerStore.selectedPartner;
  $language = this.authStore.language;
  $user = this.authStore.user;
  $isDemo = this.demoStore.isDemoMode;

  partnerDisplayFn = (p: Partner) => p?.name ?? '';

  readonly version = environment.version;
  readonly LanguageEnum = LanguageEnum;
  readonly Role = Role;
  readonly Paths = Paths;

  constructor() {
    if (this.authStore.authenticated()) {
      this.loadPartners();
    }
  }

  loadPartners(searchText?: string) {
    this.store.loadPartners(searchText);
  }

  loadMorePartners() {
    this.store.loadMorePartners();
  }

  selectPartner(partner: Partner) {
    if (partner.id === this.selectedPartnerStore.selectedPartner()?.id) {
      return;
    }
    this.selectedPartnerStore.setSelectedPartner(partner);
    this.router.navigate([Paths.PARTNER_COCKPIT_PATH, Paths.DASHBOARD_PATH]);
  }

  handleLanguageChange(language: LanguageEnum) {
    this.store.changeCurrentUserLanguage(language);
  }

  toggleDemo() {
    if (this.$isDemo()) {
      this.demoService.deactivateDemo();
    } else {
      this.demoService.activateDemo();
    }
  }

  logout(): void {
    this.authService.logout().subscribe({
      next: () => this.router.navigate([Paths.LOGIN_PATH]).then(),
      error: () => console.error('Something went wrong!'),
    });
  }

  protected readonly environment = environment;
}
